import { EventEmitter } from 'events';
import { StoreApi } from 'zustand';

import { History } from '@spazious/dollhouse-history';
import { FShallowGraph, GraphAction } from '@spazious/dollhouse-shallow-graph';
import { StagingArea } from '@spazious/dollhouse-staging-area';

import { ActionSideEffect } from './actions/index.js';

export enum CommanderActionTypesEnum {
  Init = 'init',
  Delete = 'delete',
  Duplicate = 'duplicate',
  Undo = 'undo',
  Redo = 'redo',
  Rotate = 'rotate',
  Translate = 'translate',
  Clone = 'clone',
  Unlink = 'unlink',
  Add = 'add',
  Param = 'param',
  Remove = 'remove',
  Move = 'move',
  Resize = 'resize',
  Meta = 'meta',
  Update = 'update',
  Load = 'load',
}

export enum CommanderActionPhasesEnum {
  Start = 'start',
  Update = 'update',
  End = 'end',
}

export type CommanderActionPattern = `${CommanderActionTypesEnum}:${CommanderActionPhasesEnum}`;

export interface CommanderActionEvents {
  update: [];
  commit: [];
  abort: [];
}

export interface CommanderAction<
  TType extends CommanderActionTypesEnum = CommanderActionTypesEnum,
  TPayload = any
> extends EventEmitter<CommanderActionEvents> {
  type: TType;
  phase: CommanderActionPhasesEnum | null;
  payload: TPayload | null;
  commander: Commander;
  /**
   * Update the action with a new stack of changes
   */
  update(payload: TPayload): void;
  /**
   * Commit the action this validates the action
   */
  commit(): void;
  /**
   * Abort the action this invalidates the action
   */
  abort(): void;

  toJSON(): {
    type: TType;
    payload: TPayload | null;
  };
  /**
   * Create an action from a JSON object
   */
  fromJSON(json: { type: TType; selector: string; payload: TPayload | null }): this;
}

export interface Commander<
  TState = FShallowGraph,
  TAction extends CommanderAction = CommanderAction,
  TMeta = CommanderActionTypesEnum
> {
  store: StoreApi<TState>;
  history: History<GraphAction[], TMeta>;
  staging: StagingArea<GraphAction, TMeta>;
  sideEffectHandlers?: ActionSideEffectsHandlers;
  prevState: TState;
  createAction<TType extends TAction['type']>(
    type: TType,
    ...args: any[]
  ): ActionFromType<TAction, TType>;
}

/**
 * Generic function to create an action from a type
 */
export type ActionFromType<
  TAction extends CommanderAction,
  TType extends CommanderActionTypesEnum
> = Extract<TAction, { type: TType }>;

/**
 * Extract the payload from an given action
 */
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
export type ActionPayload<TAction> = TAction extends CommanderAction<infer _, infer TPayload>
  ? TPayload
  : never;

export type ActionParameters<T extends new (c: Commander, ...args: any[]) => any> = T extends new (
  c: Commander,
  ...args: infer P
) => any
  ? P
  : never;

export type ActionSideEffectsHandlers = {
  onBeforeCommit?: (shallowGraph: FShallowGraph) => ActionSideEffect[];
};
